/*
 * Copyright (c) 2024-2026. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */

import { Company } from 'types/entities/Company'
import { CompanyContact } from 'types/entities/CompanyContact'
import {
    domainsMatch,
    extractBusinessEmailDomain,
    extractWebsiteDomain,
    isGenericEmailDomain,
} from 'utils/email/emailDomainUtils'

export type LeadAccountContactConflictDecision = 'apply_primary' | 'keep_contact' | 'conflict'

export const isContactLinkedToCompany = (
    contact: CompanyContact | null | undefined,
    company: Company | null | undefined
): boolean => {
    if (!contact?.id || !company?.id) {
        return false
    }

    if (contact.company_id === company.id) {
        return true
    }

    if ((company.contacts ?? []).some((companyContact) => companyContact.id === contact.id)) {
        return true
    }

    if ((contact.companies ?? []).some((linkedCompany) => linkedCompany.id === company.id)) {
        return true
    }

    return false
}

/** Collect business email/website domains associated with an Account. */
export const getCompanyLinkingDomains = (company: Company | null | undefined): string[] => {
    if (!company) {
        return []
    }

    const domains = new Set<string>()

    const companyEmailDomain = extractBusinessEmailDomain(company.email)
    if (companyEmailDomain) {
        domains.add(companyEmailDomain)
    }

    const websiteDomain = extractWebsiteDomain(company.website)
    if (websiteDomain && !isGenericEmailDomain(websiteDomain)) {
        domains.add(websiteDomain)
    }

    for (const companyContact of company.contacts ?? []) {
        const contactDomain = extractBusinessEmailDomain(companyContact.email)
        if (contactDomain) {
            domains.add(contactDomain)
        }
    }

    return [...domains]
}

/**
 * True when contact email domain matches Account email, website, or another Account contact.
 * Used as a soft linking signal (e.g. @svasg.ch ↔ SVA St. Gallen contacts).
 */
export const contactSharesEmailDomainWithCompany = (
    contact: Pick<CompanyContact, 'email'> | null | undefined,
    company: Company | null | undefined
): boolean => {
    const contactDomain = extractBusinessEmailDomain(contact?.email)
    if (!contactDomain) {
        return false
    }

    return getCompanyLinkingDomains(company).some((domain) => domainsMatch(domain, contactDomain))
}

export const hasDraftLeadContactFields = (fields?: {
    first_name?: string | null
    last_name?: string | null
    contact_email?: string | null
    contact_phone?: string | null
}): boolean => {
    if (!fields) {
        return false
    }

    return Boolean(
        fields.first_name?.toString().trim() ||
            fields.last_name?.toString().trim() ||
            fields.contact_email?.toString().trim() ||
            fields.contact_phone?.toString().trim()
    )
}

export const hasDraftLeadAddressFields = (fields?: {
    street_address?: string | null
    city?: string | null
    postal_code?: string | null
    state?: string | null
}): boolean => {
    if (!fields) {
        return false
    }

    return Boolean(
        fields.street_address?.toString().trim() ||
            fields.city?.toString().trim() ||
            fields.postal_code?.toString().trim() ||
            fields.state?.toString().trim()
    )
}

/**
 * Decide how Account Contact should behave when a new Account is selected.
 * - apply_primary: no current contact → fill Account primary (or clear)
 * - keep_contact: current contact already belongs to Account
 * - conflict: current contact is not linked (or draft fields filled) → confirm
 */
export const resolveLeadAccountContactConflict = (
    currentContact: CompanyContact | null | undefined,
    company: Company,
    options?: { hasDraftContactFields?: boolean }
): LeadAccountContactConflictDecision => {
    if (currentContact?.id) {
        if (isContactLinkedToCompany(currentContact, company)) {
            return 'keep_contact'
        }
        return 'conflict'
    }

    if (options?.hasDraftContactFields) {
        return 'conflict'
    }

    return 'apply_primary'
}

/** Build a display contact from form draft fields when no selected contact entity exists. */
export const buildDraftLeadContact = (fields: {
    first_name?: string | null
    last_name?: string | null
    title?: string | null
    custom_title?: string | null
    position?: string | null
    gender?: CompanyContact['gender']
    contact_email?: string | null
    contact_phone?: string | null
    contact_origin?: string | null
    contact_note?: string | null
}): CompanyContact =>
    ({
        id: 0,
        guid: '',
        company_id: null,
        address_id: null,
        first_name: fields.first_name?.toString().trim() || '',
        last_name: fields.last_name?.toString().trim() || '',
        title: fields.title?.toString().trim() || '',
        custom_title: fields.custom_title?.toString().trim() || '',
        position: fields.position?.toString().trim() || '',
        gender: fields.gender || undefined,
        email: fields.contact_email?.toString().trim() || '',
        phone: fields.contact_phone?.toString().trim() || '',
        origin: fields.contact_origin?.toString().trim() || '',
        note: fields.contact_note?.toString().trim() || '',
    }) as CompanyContact
